import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Employee } from '@kontor/shared';
import { api, ApiError, apiUpload } from '../../api';

const photoUrl = (e: Pick<Employee, 'id' | 'photoVersion'>) => `/api/v1/employees/${e.id}/photo?v=${e.photoVersion}`;
const initials = (e: Pick<Employee, 'firstName' | 'lastName'>) => `${e.firstName.trim()[0] ?? ''}${e.lastName.trim()[0] ?? ''}`.toUpperCase() || '?';

/** Kleines rundes Foto für Listen, ohne Foto die Initialen (KON-118) */
export function EmployeeAvatar({ employee, size = 40 }: { employee: Pick<Employee, 'id' | 'photoVersion' | 'firstName' | 'lastName'>; size?: number }) {
  const style = { width: size, height: size, fontSize: size * 0.38 };
  return employee.photoVersion ? (
    <img className="employee-avatar" src={photoUrl(employee)} alt="" style={style} loading="lazy" />
  ) : (
    <span className="employee-avatar employee-avatar--empty" style={style} aria-hidden="true">{initials(employee)}</span>
  );
}

/** Profilfoto in der Karte des Mitarbeiters, mit Hochladen, Ersetzen und Entfernen für alle mit hr.write (KON-118) */
export function EmployeePhoto({ employee, canWrite, onChanged }: { employee: Employee; canWrite: boolean; onChanged: () => void }) {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const done = (e: Employee) => {
    client.setQueryData(['employee', e.id], e);
    client.invalidateQueries({ queryKey: ['employees'] });
    setError(null);
    onChanged();
  };
  const upload = useMutation({
    mutationFn: async (file: File) => (await apiUpload<{ data: Employee }>(`/employees/${employee.id}/photo`, file)).data,
    onSuccess: done,
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Das Foto konnte nicht hochgeladen werden.'),
  });
  const remove = useMutation({
    mutationFn: async () => (await api<{ data: Employee }>('DELETE', `/employees/${employee.id}/photo`)).data,
    onSuccess: done,
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Das Foto konnte nicht entfernt werden.'),
  });

  return (
    <div className="employee-photo">
      <EmployeeAvatar employee={employee} size={128} />
      {canWrite && (
        <div className="employee-photo__actions">
          <label className={`btn btn--secondary btn--sm${upload.isPending ? ' is-disabled' : ''}`}>
            {upload.isPending ? 'Wird hochgeladen …' : employee.photoVersion ? 'Foto ändern' : 'Foto hochladen'}
            <input
              type="file"
              accept="image/webp,image/png,image/jpeg"
              className="sr-only"
              disabled={upload.isPending}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                if (file.size > 2 * 1024 * 1024) return setError('Das Foto ist größer als 2 MB.');
                upload.mutate(file);
              }}
            />
          </label>
          {employee.photoVersion && (
            <button type="button" className="btn btn--secondary btn--sm" disabled={remove.isPending} onClick={() => window.confirm('Profilfoto entfernen?') && remove.mutate()}>
              Entfernen
            </button>
          )}
        </div>
      )}
      {canWrite && <small className="muted">WebP, PNG oder JPEG, bis 2 MB</small>}
      {error && <small className="field__error" role="alert">{error}</small>}
    </div>
  );
}
